/**
 * AI Logistics Badge - Silent Concierge UI
 * 
 * RULES:
 * - Minimal footprint - small text strings only
 * - No chatbots, no modals, no heavy UI
 * - 100% transactional display
 * - Uses existing Space Mono typography
 */

import React from 'react';
import { Navigation, Clock, CheckCircle, AlertCircle, Sun, Moon, Compass, Cloud, CloudRain, Snowflake, Zap, Flame, TrendingUp, Users } from 'lucide-react';

// Icon mapping for badge types
const BADGE_ICONS = {
  walk: Navigation,
  clock: Clock,
  check: CheckCircle,
  alert: AlertCircle,
  sunrise: Sun,
  moon: Moon,
  compass: Compass,
  // Weather icons
  sun: Sun,
  cloud: Cloud,
  'cloud-sun': Sun,
  'cloud-rain': CloudRain,
  'cloud-drizzle': CloudRain,
  'cloud-fog': Cloud,
  'cloud-lightning': Zap,
  snowflake: Snowflake,
  // Buzz icons
  flame: Flame,
  'trending-up': TrendingUp,
  users: Users,
};

// Priority color mapping (neon accents)
const PRIORITY_COLORS = {
  high: 'text-red-400',
  medium: 'text-yellow-400',
  low: 'text-cyan-400',
  info: 'text-muted-foreground',
};

const PRIORITY_BG = {
  high: 'bg-red-400/10',
  medium: 'bg-yellow-400/10',
  low: 'bg-cyan-400/10',
  info: 'bg-foreground/5',
};

/**
 * Single AI insight badge
 */
export const AIBadge = ({ insight, size = 'sm' }) => {
  if (!insight || !insight.label) return null;
  
  const Icon = BADGE_ICONS[insight.icon] || Clock;
  const colorClass = PRIORITY_COLORS[insight.priority] || PRIORITY_COLORS.info;
  const bgClass = PRIORITY_BG[insight.priority] || PRIORITY_BG.info;
  
  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
  };
  
  return (
    <div 
      className={`inline-flex items-center gap-1 rounded ${sizeClasses[size]} ${bgClass}`}
      style={{ fontFamily: "'Space Mono', monospace" }}
    >
      <Icon className={`w-3 h-3 ${colorClass}`} />
      <span className={colorClass}>{insight.label}</span>
    </div>
  );
};

/**
 * AI Summary Badge - Most important insight
 * Displays as a single line badge
 */
export const AISummaryBadge = ({ label, className = '' }) => {
  if (!label) return null;
  
  return (
    <div 
      className={`inline-flex items-center gap-1 text-[10px] text-cyan-400/80 ${className}`}
      style={{ fontFamily: "'Space Mono', monospace" }}
    >
      <span className="opacity-60">●</span>
      <span>{label}</span>
    </div>
  );
};

/**
 * AI Insights Row - Displays multiple badges in a row
 * For use in trip bundle cards
 */
export const AIInsightsRow = ({ insights, maxDisplay = 2 }) => {
  if (!insights || insights.length === 0) return null;
  
  // Sort by priority and take top N
  const priorityOrder = { high: 0, medium: 1, low: 2, info: 3 };
  const sortedInsights = [...insights]
    .sort((a, b) => (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4))
    .slice(0, maxDisplay);
  
  return (
    <div className="flex flex-wrap items-center gap-1 mt-2">
      {sortedInsights.map((insight, idx) => (
        <AIBadge key={idx} insight={insight} size="xs" />
      ))}
    </div>
  );
};

/**
 * Venue Proximity Badge - Walk time to venue
 */
export const VenueProximityBadge = ({ distanceKm, walkMinutes }) => {
  if (!distanceKm && !walkMinutes) return null;
  
  const formatWalkTime = (mins) => {
    if (mins < 1) return '< 1 min';
    if (mins <= 5) return `${mins} min walk`;
    if (mins <= 15) return `~${mins} min walk`;
    return `~${Math.round(mins / 5) * 5} min`;
  };
  
  const label = walkMinutes 
    ? `AI: ${formatWalkTime(walkMinutes)} to venue`
    : `AI: ${distanceKm}km to venue`;
  
  return (
    <div 
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-cyan-400/10 text-cyan-400"
      style={{ fontFamily: "'Space Mono', monospace" }}
    >
      <Navigation className="w-2.5 h-2.5" />
      <span>{label}</span>
    </div>
  );
};

/**
 * Arrival Optimization Badge - For flight arrival analysis
 */
export const ArrivalOptimizationBadge = ({ insight }) => {
  if (!insight || insight.type !== 'arrival_optimization') return null;
  
  const categoryColors = {
    dead_zone: 'text-red-400 bg-red-400/10',
    early_morning: 'text-yellow-400 bg-yellow-400/10',
    morning: 'text-cyan-400 bg-cyan-400/10',
    perfect: 'text-green-400 bg-green-400/10',
    tight: 'text-orange-400 bg-orange-400/10',
  };
  
  const colorClass = categoryColors[insight.category] || categoryColors.morning;
  const Icon = BADGE_ICONS[insight.icon] || Clock;
  
  return (
    <div 
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${colorClass}`}
      style={{ fontFamily: "'Space Mono', monospace" }}
    >
      <Icon className="w-2.5 h-2.5" />
      <span>{insight.label}</span>
    </div>
  );
};

/**
 * Weather Badge - Live weather conditions
 */
export const WeatherBadge = ({ insight }) => {
  if (!insight || insight.type !== 'weather') return null;
  
  const Icon = BADGE_ICONS[insight.icon] || Cloud;
  const colorClass = PRIORITY_COLORS[insight.priority] || PRIORITY_COLORS.info;
  const bgClass = PRIORITY_BG[insight.priority] || PRIORITY_BG.info;
  
  return (
    <div 
      className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded ${bgClass}`}
      style={{ fontFamily: "'Space Mono', monospace" }}
      title={insight.detail}
    >
      {insight.emoji && <span>{insight.emoji}</span>}
      <Icon className={`w-3 h-3 ${colorClass}`} />
      <span className={colorClass}>{insight.label}</span>
    </div>
  );
};

/**
 * Buzz Badge - Social demand indicator
 */
export const BuzzBadge = ({ insight }) => {
  if (!insight || insight.type !== 'buzz') return null;
  
  const Icon = BADGE_ICONS[insight.icon] || Flame;
  const colorClass = PRIORITY_COLORS[insight.priority] || PRIORITY_COLORS.info;
  const bgClass = PRIORITY_BG[insight.priority] || PRIORITY_BG.info;
  
  return (
    <div 
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${bgClass}`}
      style={{ fontFamily: "'Space Mono', monospace" }}
      title={insight.detail}
    >
      <Icon className={`w-2.5 h-2.5 ${colorClass}`} />
      <span className={colorClass}>{insight.label}</span>
    </div>
  );
};

export default AIBadge;
